import React, { useEffect, useMemo, useState } from 'react';
import HowPacketsDrawer from './HowPacketsDrawer';
import { formatDisplayWeekdayDate } from '../utils/displayDate';

/**
 * Delivery Portal homepage — calm tool hub with operational context.
 * Left = tools + upcoming · Right = ops note + quick reference.
 */

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseISO(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
}

function addDaysISO(iso, n) {
  const d = parseISO(iso);
  if (!d) return iso;
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDateLong(d = new Date()) {
  // "Wed, Oct 28, 2026" — the shared standard with the weekday.
  return formatDisplayWeekdayDate(formatDisplayInstantDateYmd(d), { withYear: true });
}

/** Today's calendar date in Central (the team's day), as YYYY-MM-DD. */
function formatDisplayInstantDateYmd(d) {
  const p = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
  const part = (t) => p.find((x) => x.type === t).value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

function formatShortDay(iso) {
  const d = parseISO(iso);
  if (!d) return iso;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function timeOfDayGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function firstNameFromEmail(email) {
  if (!email) return '';
  const local = String(email).split('@')[0] || '';
  const token = local.split(/[._-]/)[0] || local;
  if (!token) return '';
  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
}

const UPCOMING_VISIBLE = 3;
const SCHEDULE_SOURCES_CACHE_KEY = 'delivery_schedule_sources_v1';
const SCHEDULE_SOURCES_CACHE_TS = 'delivery_schedule_sources_ts_v1';
const SCHEDULE_SOURCES_TTL_MS = 5 * 60 * 1000;

function readCachedScheduleSources() {
  try {
    const ts = Number(sessionStorage.getItem(SCHEDULE_SOURCES_CACHE_TS) || '0');
    if (!ts || Date.now() - ts > SCHEDULE_SOURCES_TTL_MS) return null;
    const raw = sessionStorage.getItem(SCHEDULE_SOURCES_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function writeCachedScheduleSources(payload) {
  try {
    sessionStorage.setItem(SCHEDULE_SOURCES_CACHE_KEY, JSON.stringify(payload));
    sessionStorage.setItem(SCHEDULE_SOURCES_CACHE_TS, String(Date.now()));
  } catch (_) {
    /* quota */
  }
}

/** Operation time from the field that belongs to this schedule source row. */
function opTime(row) {
  if (row.kind === 'delivery') return String(row.deliveryTime || '').trim();
  if (row.kind === 'loadout') return String(row.strikeTime || '').trim();
  if (row.kind === 'field') return String(row.onSiteTime || '').trim();
  return '';
}

/**
 * Secondary metadata from reliable source fields only.
 * EPS → EVENT · [venue] · [time]
 * Staging → HOME STAGING · LOAD IN|LOAD OUT · [time]
 */
function opMeta(row) {
  const parts = [];
  const isStaging = row.source === 'Staging';
  if (isStaging) {
    parts.push('HOME STAGING');
    if (row.kind === 'delivery') parts.push('LOAD IN');
    else if (row.kind === 'loadout') parts.push('LOAD OUT');
  } else if (row.source === 'EPS') {
    parts.push('EVENT');
  }
  const venue = String(row.venue || '').trim();
  if (venue && !isStaging) parts.push(venue);
  const time = opTime(row);
  if (time) parts.push(time);
  return parts.join(' · ');
}

/** Prefer clock-ish times when sorting within a day; undated sink last. */
function timeSortKey(row) {
  const t = opTime(row);
  if (!t) return 9999;
  const m = t.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  if (!m) return 5000;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const ap = (m[3] || '').toUpperCase();
  if (ap === 'PM' && h < 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  return h * 60 + min;
}

const PRIMARY_TOOLS = [
  {
    id: 'schedule',
    title: 'Daily Production Plan',
    description: 'Build and publish the team’s daily delivery schedule.',
    tab: 'schedule',
  },
  {
    id: 'packets',
    title: 'Production Packets',
    description: 'Create detailed run sheets for upcoming projects.',
    tab: 'packets',
  },
  {
    id: 'calculator',
    title: 'Delivery Fee Calculator',
    description: 'Calculate delivery pricing for individual projects or multi-event programs.',
    tab: 'calculator',
  },
  {
    id: 'expenses',
    title: 'Submit Expenses',
    description: 'Log delivery-related project or program expenses.',
    tab: 'expenses',
  },
];

function Home({ userEmail = '', scheduleApiUrl = '', onNavigate, onOpenQuickEstimate }) {
  const [sources, setSources] = useState(null);
  const [upcomingState, setUpcomingState] = useState(scheduleApiUrl ? 'loading' : 'empty');
  const [howOpen, setHowOpen] = useState(false);
  const firstName = firstNameFromEmail(userEmail);
  const today = todayISO();

  useEffect(() => {
    if (!scheduleApiUrl) {
      setUpcomingState('empty');
      return undefined;
    }
    let cancelled = false;
    const cached = readCachedScheduleSources();
    if (cached) {
      setSources(cached);
      setUpcomingState('ready');
    } else {
      setUpcomingState('loading');
    }
    (async () => {
      try {
        const resp = await fetch(`${scheduleApiUrl}?action=getScheduleSources`);
        const text = await resp.text();
        const trimmed = text.trim();
        if (!trimmed || trimmed.charAt(0) === '<') {
          if (!cached && !cancelled) setUpcomingState('error');
          return;
        }
        const data = JSON.parse(text);
        if (cancelled) return;
        if (data.success) {
          const next = {
            deliveries: data.deliveries || [],
            loadouts: data.loadouts || [],
            sideQuests: data.sideQuests || [],
          };
          setSources(next);
          writeCachedScheduleSources(next);
          setUpcomingState('ready');
        } else if (!cached) {
          setUpcomingState('error');
        }
      } catch (_) {
        if (!cancelled && !cached) setUpcomingState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [scheduleApiUrl]);

  useEffect(() => {
    if (!howOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setHowOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [howOpen]);

  const upcoming = useMemo(() => {
    if (!sources) return [];
    const days = [1, 2, 3, 4, 5, 6, 7].map((n) => addDaysISO(today, n));
    const groups = [];
    days.forEach((iso) => {
      const rows = [
        ...sources.deliveries.filter((r) => r.date === iso).map((r) => ({ ...r, kind: 'delivery' })),
        ...sources.loadouts.filter((r) => r.date === iso).map((r) => ({ ...r, kind: 'loadout' })),
        ...sources.sideQuests.filter((r) => r.date === iso).map((r) => ({ ...r, kind: 'field' })),
      ]
        .filter((r) => r.project)
        .sort((a, b) => timeSortKey(a) - timeSortKey(b) || String(a.project).localeCompare(String(b.project)));

      if (!rows.length) return;

      const more = Math.max(0, rows.length - UPCOMING_VISIBLE);
      groups.push({
        iso,
        label: formatShortDay(iso).toUpperCase(),
        ops: rows.slice(0, UPCOMING_VISIBLE).map((r) => ({
          key: `${r.kind}-${r.project}-${r.rwTicket || ''}`,
          title: r.project,
          meta: opMeta(r),
        })),
        more,
      });
    });
    return groups.slice(0, 6);
  }, [sources, today]);

  const go = (tab) => {
    if (tab && typeof onNavigate === 'function') onNavigate(tab);
  };

  return (
    <div className="briefing">
      <div className="brief-grid">
        <div className="brief-col-main">
          <header className="brief-hero">
            <p className="brief-date">{formatDateLong()}</p>
            <h1 className="brief-greeting">
              {timeOfDayGreeting()}{firstName ? `, ${firstName}` : ''}.
            </h1>
            <p className="brief-support">
              Tools and resources for planning, pricing, executing, and closing out deliveries.
            </p>
          </header>

          <section className="brief-section brief-tools" aria-labelledby="brief-tools">
            <h2 id="brief-tools" className="brief-section-label">Tools</h2>
            <ul className="brief-tool-list">
              {PRIMARY_TOOLS.map((tool) => (
                <li key={tool.id} className="brief-tool">
                  <button type="button" className="brief-tool-link" onClick={() => go(tool.tab)}>
                    <span className="brief-tool-body">
                      <span className="brief-tool-title">{tool.title}</span>
                      <span className="brief-tool-desc">{tool.description}</span>
                    </span>
                    <span className="brief-tool-cta">
                      Open
                      <span aria-hidden> →</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>

          <section className="brief-section brief-upcoming-sec" aria-labelledby="brief-upcoming">
            <h2 id="brief-upcoming" className="brief-section-label">Upcoming Operations</h2>

            {upcomingState === 'loading' && (
              <div className="brief-upcoming-loading" aria-live="polite" aria-busy="true">
                <p className="brief-upcoming-status">Projects are loading…</p>
                <div className="brief-upcoming brief-upcoming--skeleton">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="brief-upcoming-group brief-skel-group">
                      <div className="brief-skel brief-skel--date" />
                      <div className="brief-skel brief-skel--title" />
                      <div className="brief-skel brief-skel--meta" />
                      <div className="brief-skel brief-skel--title" />
                      <div className="brief-skel brief-skel--meta" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {upcomingState === 'error' && (
              <p className="brief-upcoming-status">Upcoming operations could not be loaded.</p>
            )}

            {upcomingState === 'ready' && upcoming.length === 0 && (
              <p className="brief-upcoming-status">No upcoming operations scheduled.</p>
            )}

            {upcomingState === 'ready' && upcoming.length > 0 && (
              <div className="brief-upcoming">
                {upcoming.map((group) => (
                  <div key={group.iso} className="brief-upcoming-group">
                    <h3 className="brief-upcoming-label">{group.label}</h3>
                    <ul className="brief-ops">
                      {group.ops.map((op) => (
                        <li key={op.key} className="brief-op">
                          <button type="button" className="brief-op-link" onClick={() => go('schedule')}>
                            <span className="brief-op-dot" aria-hidden="true" />
                            <span className="brief-op-body">
                              <span className="brief-op-title">{op.title}</span>
                              {op.meta && <span className="brief-op-meta">{op.meta}</span>}
                            </span>
                          </button>
                        </li>
                      ))}
                      {group.more > 0 && (
                        <li className="brief-op brief-op--more">
                          <button type="button" className="brief-ops-more" onClick={() => go('schedule')}>
                            + {group.more} MORE
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {upcomingState === 'empty' && (
              <p className="brief-upcoming-status">No upcoming operations scheduled.</p>
            )}
          </section>
        </div>

        <aside className="brief-col-aside">
          <section className="brief-quote" aria-labelledby="brief-quote-label">
            <p id="brief-quote-label" className="brief-section-label brief-quote-kicker">
              From the Field
            </p>
            <blockquote className="brief-quote-text">
              “A rising tide lifts all boats.”
            </blockquote>
            <p className="brief-quote-attr">Popularized by John F. Kennedy</p>
            <img
              className="brief-quote-sketch"
              src="/boat-sketch.png"
              alt=""
              width={180}
              height={137}
              decoding="async"
              aria-hidden="true"
            />
          </section>

          <section
            className="brief-rec-rail brief-rec-rail--after-founder"
            aria-labelledby="brief-reference"
          >
            <h2 id="brief-reference" className="brief-section-label brief-rec-rail-label">
              Quick Reference
            </h2>
            <ul className="brief-rec-list">
              <li className="brief-rec-item">
                <button
                  type="button"
                  className="brief-rec-item-link"
                  onClick={() => go('guidelines')}
                >
                  <span className="brief-rec-title">Delivery Guidelines</span>
                  <span className="brief-rec-desc">
                    Minimums, pricing rules, and delivery standards.
                  </span>
                  <span className="brief-rec-link">
                    Open
                    <span aria-hidden> →</span>
                  </span>
                </button>
              </li>
              <li className="brief-rec-item">
                <button
                  type="button"
                  className="brief-rec-item-link"
                  onClick={() => setHowOpen(true)}
                >
                  <span className="brief-rec-title">How Production Packets Work</span>
                  <span className="brief-rec-desc">
                    Understand where packet information comes from and what is completed in the Delivery Portal.
                  </span>
                  <span className="brief-rec-link">
                    Open
                    <span aria-hidden> →</span>
                  </span>
                </button>
              </li>
              <li className="brief-rec-item">
                <button
                  type="button"
                  className="brief-rec-item-link"
                  onClick={() => onOpenQuickEstimate && onOpenQuickEstimate()}
                >
                  <span className="brief-rec-title">Quick Delivery Estimate</span>
                  <span className="brief-rec-desc">
                    Plan delivery using the same fee rules as the proposal quote.
                  </span>
                  <span className="brief-rec-link">
                    Open
                    <span aria-hidden> →</span>
                  </span>
                </button>
              </li>
            </ul>
          </section>
        </aside>
      </div>

      {howOpen && <HowPacketsDrawer onClose={() => setHowOpen(false)} />}
    </div>
  );
}

export default Home;
